<template>
    <x-page title="Report Absensi">
        <template #header-actions>
            <x-back-button
                :disabled="isLoading"
                :display-warning="false"
                :click-handler="handleBackButtonClick"
            />
            <x-excel-export-button :href="exportUrl" :disabled="isLoading" />
        </template>

        <x-filter-box
            :onFilter="handleFilter"
            :onReset="resetFilter"
            :open="true"
            :readonly="isLoading"
        >
            <div class="row">
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Dari Tanggal">
                        <input
                            id="with_count_gathering_from"
                            type="date"
                            class="form-control"
                            v-model="filters.with_count_gathering_from"
                        />
                    </x-form-group>
                </div>
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Sampai Tanggal">
                        <input
                            id="with_count_gathering_to"
                            type="date"
                            class="form-control"
                            v-model="filters.with_count_gathering_to"
                        />
                    </x-form-group>
                </div>
            </div>
            <div class="row">
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Kehadiran Minimal">
                        <x-input-group>
                            <input
                                type="number"
                                class="form-control"
                                v-model="gathering_percentage_min"
                            />
                            <template #append>%</template>
                        </x-input-group>
                    </x-form-group>
                </div>
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Kehadiran Maksimal">
                        <x-input-group>
                            <input
                                type="number"
                                class="form-control"
                                v-model="gathering_percentage_max"
                            />
                            <template #append>%</template>
                        </x-input-group>
                    </x-form-group>
                </div>
            </div>
            <div class="row">
                <div class="col-xs-12 col-md-6">
                    <x-form-group
                        label="Group"
                        :help-text="
                            Boolean(group) &&
                            Boolean(group.last_gathering_update)
                                ? `Update terakhir ${$options.filters.date(
                                      group.last_gathering_update
                                  )}`
                                : null
                        "
                    >
                        <input
                            type="text"
                            class="form-control"
                            :value="Boolean(group) ? group.label : '-'"
                            disabled
                        />
                    </x-form-group>
                </div>
                <div class="col-xs-12 col-md-6">
                    <x-form-group label="Tergabung Ke Dalam">
                        <x-ajax-dropdown
                            url="/api/groups"
                            label="label"
                            :allow-empty="true"
                            v-model="otherGroup"
                            :blacklist="[$route.params.id]"
                            class="form-control"
                            placeholder="Group lain"
                        />
                    </x-form-group>
                </div>
            </div>
        </x-filter-box>

        <x-table
            :headers="['#', 'ID', 'Nama Lengkap', 'Jenis Kelamin', 'Kehadiran']"
            :is-loading="isLoading || !Boolean(group)"
        >
            <tr
                v-for="(resource, index) in resources"
                :key="index"
                :style="{
                    'background-color': getBackgroundColor(resource),
                }"
            >
                <td>{{ rowIndex(index, $route.query) }}</td>
                <td class="nowrap">
                    {{ resource.id }}
                </td>
                <td class="nowrap">
                    {{ resource.name }}
                </td>
                <td class="nowrap">
                    {{ resource.gender }}
                </td>
                <td>
                    <span
                        :title="
                            Boolean(group)
                                ? `${resource.gatherings_count} dari ${group.gatherings_count}`
                                : null
                        "
                        class="hint"
                    >
                        {{ calculateAttendance(resource) }}%
                    </span>
                </td>
            </tr>
        </x-table>

        <x-pagination
            v-if="shouldRenderPagination"
            :pagination="meta"
            :query="$route.query"
        />
    </x-page>
</template>

<script>
import XAjaxDropdown from "../../../components/XAjaxDropdown.vue";
import XBackButton from "../../../components/XBackButton.vue";
import XExcelExportButton from "../../../components/XExcelExportButton.vue";
import XFilterBox from "../../../components/XFilterBox.vue";
import XFormGroup from "../../../components/XFormGroup.vue";
import XInputGroup from "../../../components/XInputGroup.vue";
import XPage from "../../../components/XPage.vue";
import XPagination from "../../../components/XPagination.vue";
import XTable from "../../../components/XTable.vue";
import { dateFormat } from "../../../constants";
import { attendanceIndexUrl, handleGeneralXhrError } from "../../../helpers";
import FilterableMixin from "../../../mixins/FilterableMixin";
import PaginationMixin from "../../../mixins/PaginationMixin";
import PermissionMixin from "../../../mixins/PermissionMixin";

let defaultFilters = {
    gathering_count_min: 0,
    gathering_count_max: 0,
    is_reportable_in_group: null,
    other_group_id: null,
    with_count_gathering_from: moment()
        .clone()
        .startOf("month")
        .format(dateFormat),
    with_count_gathering_to: moment().clone().endOf("month").format(dateFormat),
};

export default {
    beforeRouteUpdate(to, from, next) {
        this.fetchGroup(to);
        this.fetchResources(to);
        next();
    },
    components: {
        XTable,
        XPage,
        XFormGroup,
        XFilterBox,
        XAjaxDropdown,
        XPagination,
        XBackButton,
        XInputGroup,
        XExcelExportButton,
    },
    computed: {
        exportUrl() {
            return `/admin/reports/attendances/${this.group?.id}/excel-export${window.location.search}`;
        },
    },
    created() {
        this.fetchColorCoding();
        this.fetchGroup().then(() => {
            defaultFilters.is_reportable_in_group = this.$route.params.id;
            this.preselectFilters(defaultFilters);
            this.preselectGatheringPercentages();
            this.handleFilter().catch(() => {
                this.fetchResources();
            });
        });
    },
    data() {
        return {
            colorCoding: [],
            filters: { ...defaultFilters },
            gathering_percentage_min: 0,
            gathering_percentage_max: 100,
            group: null,
            isLoading: false,
            resources: [],
            otherGroup: [],
        };
    },
    methods: {
        calculateAttendance(member) {
            if (!Boolean(this.group)) {
                return 100;
            }

            const result =
                (member.gatherings_count / this.group.gatherings_count) * 100;

            return isNaN(result) ? 0 : _.round(result, 2);
        },
        fetchColorCoding() {
            this.isLoading = true;
            const request = axios
                .get("/api/settings", {
                    params: {
                        key: "color_coding",
                        parse: 1,
                    },
                })
                .then((response) => {
                    this.colorCoding = response.data.value;
                });
            return handleGeneralXhrError(request).finally(() => {
                this.isLoading = false;
            });
        },
        fetchGroup(route = this.$route) {
            this.isLoading = true;
            const request = axios
                .get(`/api/groups/${this.$route.params.id}`, {
                    params: {
                        append: ["last_gathering_update"],
                        with_count_gathering_from:
                            route.query.with_count_gathering_from,
                        with_count_gathering_to:
                            route.query.with_count_gathering_to,
                    },
                })
                .then((response) => {
                    this.group = response.data.data;
                    defaultFilters.gathering_count_max =
                        this.group.gatherings_count;
                });
            return handleGeneralXhrError(request).finally(() => {
                this.isLoading = false;
            });
        },
        fetchResources(route = this.$route) {
            this.isLoading = true;
            const request = axios
                .get("/api/members", {
                    params: route.query,
                })
                .then((response) => {
                    this.resources = response.data.data;
                    this.setMeta(response);
                });
            return handleGeneralXhrError(request).finally(() => {
                this.isLoading = false;
            });
        },
        getBackgroundColor(member) {
            const attendance = this.calculateAttendance(member);
            const colorCode = this.colorCoding.find((code) => {
                return (
                    attendance >= parseFloat(code.percentage_min) &&
                    attendance <= parseFloat(code.percentage_max)
                );
            });
            return colorCode?.color || null;
        },
        handleBackButtonClick(router) {
            router.push(attendanceIndexUrl());
        },
        handleFilter() {
            const { other_group_id, ...filters } = this.filters;
            return this.$router.push({
                path: this.$route.path,
                query: {
                    ...filters,
                    gathering_count_max:
                        this.gathering_percentage_max >= 100
                            ? null
                            : filters.gathering_count_max,
                    group_ids: [other_group_id].filter((id) => Boolean(id)),
                    page: 1,
                },
            });
        },
        preselectGatheringPercentages() {
            if (!Boolean(this.$route.query.gathering_count_max)) {
                this.filters.gathering_count_max =
                    defaultFilters.gathering_count_max;
            } else {
                this.gathering_percentage_max =
                    (this.filters.gathering_count_max /
                        this.group.gatherings_count) *
                    100;
            }

            if (!Boolean(this.$route.query.gathering_count_min)) {
                this.filters.gathering_count_min =
                    defaultFilters.gathering_count_min;
            } else {
                this.gathering_percentage_min =
                    (this.filters.gathering_count_min /
                        this.group.gatherings_count) *
                    100;
            }
        },
        resetFilter() {
            this.filters = { ...defaultFilters };
            this.handleFilter().then(() => {
                this.preselectGatheringPercentages();
            });
        },
    },
    mixins: [FilterableMixin, PaginationMixin, PermissionMixin],
    watch: {
        "filters.other_group_id": {
            handler(id) {
                this.syncFilterToProperty(
                    id,
                    "otherGroup",
                    `/api/groups/${id}`
                );
            },
            immediate: true,
        },
        gathering_percentage_max: {
            handler(percentage) {
                const countMax = _.get(this.group, "gatherings_count", 0);
                const count = parseInt((countMax * percentage) / 100);
                if (count === this.filters.gathering_count_max) {
                    return;
                }
                this.filters.gathering_count_max = count;
            },
        },
        gathering_percentage_min: {
            handler(percentage) {
                const countMax = _.get(this.group, "gatherings_count", 0);
                const count = (countMax * percentage) / 100;
                if (count == this.filters.gathering_count_min) {
                    return;
                }
                this.filters.gathering_count_min = count;
            },
        },
        otherGroup: {
            handler(resource) {
                this.syncPropertyToFilter(resource, "other_group_id");
            },
            deep: true,
        },
    },
};
</script>
